import { useCallback, useEffect, useRef, useState } from "react";

import { apiFetch } from "@pontimed/api-client";
import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { HistoryPanel } from "@/components/admin/testing/history-panel";
import { loadHistory, newRunId, saveRun } from "@/components/admin/testing/history-store";
import { LighthousePanel } from "@/components/admin/testing/lighthouse-panel";
import {
  eventNumber,
  eventString,
  streamTestingRun,
  type TestingEvent,
} from "@/components/admin/testing/sse";
import type {
  HistoryRun,
  LighthouseFormState,
  LighthouseSummary,
  LogEntry,
  PlaywrightResult,
  PlaywrightStats,
  RunStatus,
  TestSuite,
  TestingRunnerStatus,
} from "@/components/admin/testing/types";
import { useAdminContext } from "@/lib/admin-context";

/**
 * Admin → Testing.
 *
 * 3-tab shell: Run / History / Lighthouse
 *   - Per-test re-run buttons in the result table
 *   - Run history persisted to localStorage with detail drilldown
 *   - Lighthouse runner UI (URL + form factor + categories)
 *
 * Endpoints (both SSE-streaming, backed by apps/testing-workers):
 *   - POST /api/admin/testing/playwright  { env, suite, test? }
 *   - POST /api/admin/testing/lighthouse  { env, url, formFactor, categories }
 *
 * apps/testing-workers is a local-only container that is deployed nowhere, so
 * before anything is queued the page asks GET /api/admin/testing/runner-status
 * whether a consumer is listening and shows a banner when none is. A run
 * triggered anyway comes back as a single `error` event carrying
 * `code: "no_runner"` — never a stream that hangs until the 10-minute watchdog.
 *
 * The SSE event names come from the worker (`start`/`progress`/`test-failed`/
 * `summary-line`/`complete`/…), same vocabulary the legacy admin page
 * consumed — see `@/components/admin/testing/sse`. Target env follows the
 * local/prod toggle in the admin header, like legacy did.
 */

type TestingTab = "run" | "history" | "lighthouse";

interface TestingSearch {
  tab?: TestingTab;
}

const VALID_TABS: TestingTab[] = ["run", "history", "lighthouse"];

function isValidTab(value: string): value is TestingTab {
  return (VALID_TABS as string[]).includes(value);
}

export const Route = createFileRoute("/_authenticated/testing")({
  validateSearch: (search: Record<string, unknown>): TestingSearch => {
    const raw = typeof search.tab === "string" ? search.tab : undefined;
    return { tab: raw && isValidTab(raw) ? raw : undefined };
  },
  component: TestingPage,
});

/**
 * Playwright's `line` reporter prints full titles; the worker's fail list and
 * the progress lines can be truncated differently. Legacy matched on a 30-char
 * prefix in either direction — kept 1:1 so the same runs keep resolving.
 */
function isSameTest(a: string, b: string): boolean {
  return a === b || a.includes(b.slice(0, 30)) || b.includes(a.slice(0, 30));
}

function TestingPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const activeTab: TestingTab = search.tab ?? "run";
  const switchTab = (tab: TestingTab) => {
    void navigate({ search: () => ({ tab }), replace: true });
  };

  const { t } = useLingui();
  const { dbTarget } = useAdminContext();
  const [suite, setSuite] = useState<TestSuite>("public");
  const [playwrightStatus, setPlaywrightStatus] = useState<RunStatus>("idle");
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [playwrightResults, setPlaywrightResults] = useState<PlaywrightResult[]>([]);
  const [playwrightStats, setPlaywrightStats] = useState<PlaywrightStats | null>(null);
  const [history, setHistory] = useState<HistoryRun[]>([]);
  const [activeRerun, setActiveRerun] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const logRef = useRef<HTMLDivElement>(null);
  const runStartRef = useRef<number>(0);
  // Mirror of `playwrightResults` the event handlers mutate. Deriving the next
  // list from a ref (instead of side-effecting inside a setState updater)
  // keeps the reducer pure — StrictMode double-invokes updaters in dev.
  const resultsRef = useRef<PlaywrightResult[]>([]);

  // Is anything going to pick a run up? The container is started by hand on a
  // developer machine, so poll — the banner has to clear on its own once it
  // boots, and appear on its own when it is stopped mid-session.
  const runnerQuery = useQuery({
    queryKey: ["admin", "testing", "runner-status"],
    queryFn: () => apiFetch.get<TestingRunnerStatus>("/api/admin/testing/runner-status"),
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
  const refetchRunner = runnerQuery.refetch;
  const runner = runnerQuery.data;
  /** Confidently nobody home. */
  const runnerOffline = !!runner && runner.detectable && !runner.connected;
  /** apps/api could not probe — unknown is not the same as absent. */
  const runnerUnknown = !!runner && !runner.detectable;

  useEffect(() => {
    setHistory(loadHistory());
  }, []);

  const addLog = useCallback((type: LogEntry["type"], message: string) => {
    setLogs((prev) => {
      const entry = { time: new Date().toLocaleTimeString(), type, message };
      const next = [...prev, entry];
      setTimeout(() => logRef.current?.scrollTo(0, logRef.current.scrollHeight), 50);
      return next.slice(-500);
    });
  }, []);

  const commitResults = useCallback((next: PlaywrightResult[]) => {
    resultsRef.current = next;
    setPlaywrightResults(next);
  }, []);

  const persistRun = useCallback(
    (
      kind: HistoryRun["kind"],
      results: PlaywrightResult[],
      stats: PlaywrightStats,
      extras: Partial<HistoryRun> = {},
    ) => {
      const finishedAt = Date.now();
      const startedAt = runStartRef.current || finishedAt;
      const run: HistoryRun = {
        id: newRunId(),
        startedAt: new Date(startedAt).toISOString(),
        finishedAt: new Date(finishedAt).toISOString(),
        durationMs: finishedAt - startedAt,
        kind,
        suite,
        results,
        stats,
        ...extras,
      };
      setHistory(saveRun(run));
    },
    [suite],
  );

  /**
   * Translate one worker event into console + table state.
   *
   * Playwright's `line` reporter only announces a test when it STARTS, and
   * only prints the ones that failed afterwards — so a row goes
   * running → passed on the next `progress`, and is flipped back to failed by
   * `test-failed` / the `failedTests` list on `complete`.
   */
  const handlePlaywrightEvent = useCallback(
    (event: TestingEvent) => {
      switch (event.type) {
        case "start":
          addLog("info", t`Target: ${eventString(event, "baseUrl") ?? "—"}`);
          break;

        case "info":
          addLog("info", eventString(event, "message") ?? "");
          break;

        case "progress": {
          const testName = eventString(event, "test");
          const current = eventNumber(event, "current");
          const total = eventNumber(event, "total");
          if (testName && current) {
            const advanced = resultsRef.current.map((r) =>
              r.status === "running" ? { ...r, status: "passed" as const } : r,
            );
            commitResults([...advanced, { test: testName, status: "running" }]);
            addLog("progress", `[${current}/${total ?? "?"}] ${testName}`);
          } else {
            addLog("progress", eventString(event, "message") ?? "");
          }
          break;
        }

        case "test-failed": {
          const failed = eventString(event, "test");
          if (!failed) break;
          commitResults(
            resultsRef.current.map((r) =>
              r.test === failed || (r.status === "running" && isSameTest(r.test, failed))
                ? { ...r, status: "failed" as const }
                : r,
            ),
          );
          addLog("error", t`FAILED: ${failed}`);
          break;
        }

        case "summary-line": {
          const count = eventNumber(event, "count") ?? 0;
          const status = eventString(event, "status") ?? "";
          addLog(status === "failed" ? "error" : "success", `${count} ${status}`);
          break;
        }

        case "error-detail":
        case "stderr":
          addLog("error", eventString(event, "message") ?? "");
          break;

        case "log":
          addLog("info", eventString(event, "message") ?? "");
          break;

        case "complete": {
          const raw = event.failedTests;
          const failedList = Array.isArray(raw)
            ? raw.filter((f): f is string => typeof f === "string")
            : [];
          const finalized = resultsRef.current.map((r) => {
            if (failedList.some((f) => isSameTest(r.test, f))) {
              return { ...r, status: "failed" as const };
            }
            return r.status === "running" ? { ...r, status: "passed" as const } : r;
          });
          commitResults(finalized);
          setPlaywrightStats({
            passed: finalized.filter((r) => r.status === "passed").length,
            failed: finalized.filter((r) => r.status === "failed").length,
            total: finalized.length,
          });
          const exitCode = eventNumber(event, "exitCode");
          addLog(
            exitCode === 0 ? "success" : "error",
            exitCode === 0
              ? t`All tests passed`
              : t`${failedList.length} test(s) failed (exit code ${exitCode ?? "?"})`,
          );
          setPlaywrightStatus("complete");
          break;
        }

        case "error": {
          // apps/api refuses a run when no consumer is draining the queue —
          // translate that one, the rest carry their own runner text.
          if (eventString(event, "code") === "no_runner") {
            addLog("error", t`No test runner is connected — the run was not queued.`);
            void refetchRunner();
          } else {
            addLog("error", eventString(event, "message") ?? t`Unknown error`);
          }
          setPlaywrightStatus("error");
          break;
        }

        default:
          break;
      }
    },
    [addLog, commitResults, refetchRunner, t],
  );

  const runPlaywright = async (singleTest?: string) => {
    setPlaywrightStatus("running");
    runStartRef.current = Date.now();
    commitResults([]);
    setPlaywrightStats(null);
    if (singleTest) {
      setActiveRerun(singleTest);
      addLog("info", t`Re-run requested: ${singleTest}`);
    } else {
      addLog("info", t`Running Playwright suite "${suite}" against ${dbTarget}`);
    }

    const controller = new AbortController();
    abortRef.current = controller;
    const result = await streamTestingRun(
      "/api/admin/testing/playwright",
      singleTest ? { env: dbTarget, suite, test: singleTest } : { env: dbTarget, suite },
      handlePlaywrightEvent,
      controller.signal,
    );
    abortRef.current = null;
    setActiveRerun(null);

    if (!result.ok) {
      if (result.aborted) {
        setPlaywrightStatus("idle");
        return;
      }
      addLog("error", result.error ?? t`Stream failed`);
      setPlaywrightStatus("error");
      return;
    }
    setPlaywrightStatus((s) => (s === "running" ? "complete" : s));

    const finalResults = resultsRef.current;
    // A refused run (no runner connected) streams one error frame and stops.
    // Nothing ran, so don't file a 0/0/0 entry in history.
    if (finalResults.length === 0) return;

    const passed = finalResults.filter((r) => r.status === "passed").length;
    const failed = finalResults.filter((r) => r.status === "failed").length;
    persistRun(
      singleTest ? "playwright-single" : "playwright",
      finalResults,
      { passed, failed, total: finalResults.length },
      singleTest ? { singleTestName: singleTest } : {},
    );
  };

  const cancel = () => {
    abortRef.current?.abort();
    setPlaywrightStatus("idle");
    setActiveRerun(null);
    addLog("info", t`Run cancelled`);
  };

  const clearResults = () => {
    setLogs([]);
    commitResults([]);
    setPlaywrightStats(null);
  };

  const copyLogs = async () => {
    const text = logs.map((l) => `${l.time} ${l.message}`).join("\n");
    await navigator.clipboard.writeText(text);
  };

  const onLighthouseComplete = (
    form: LighthouseFormState,
    outcome: { summary?: LighthouseSummary; startedAt: number },
  ) => {
    const summary = outcome.summary;
    // No summary = no audit happened (refused run, or a stream that died
    // before `complete`). Nothing to show in the detail dialog, so don't
    // record it.
    if (!summary) return;

    const finishedAt = Date.now();
    const startedAt = outcome.startedAt || finishedAt;
    const run: HistoryRun = {
      id: newRunId(),
      startedAt: new Date(startedAt).toISOString(),
      finishedAt: new Date(finishedAt).toISOString(),
      durationMs: finishedAt - startedAt,
      kind: "lighthouse",
      results: [],
      stats: {
        passed: summary.passed,
        failed: summary.failed,
        total: summary.total,
      },
      lighthouseUrl: form.url,
      lighthouseFormFactor: form.formFactor,
      lighthouseCategories: form.categories,
      lighthouseSummary: summary,
    };
    setHistory(saveRun(run));
  };

  const isRunning = playwrightStatus === "running";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">
          <Trans>Testing</Trans>
        </h1>
        <p className="text-muted-foreground text-sm">
          <Trans>
            Playwright e2e + Lighthouse audits with per-test re-run, persisted history, streaming
            output.
          </Trans>{" "}
          <Trans>
            Target: <span className="text-foreground font-semibold">{dbTarget}</span> (switch it
            from the header toggle).
          </Trans>
        </p>
      </div>

      {(runnerOffline || runnerUnknown) && (
        <div
          role="status"
          className="border-warning-border bg-warning-soft text-warning-strong rounded-xl border p-4"
        >
          <p className="text-sm font-semibold">
            {runnerOffline ? (
              <Trans>No test runner is connected</Trans>
            ) : (
              <Trans>Test runner status unknown</Trans>
            )}
          </p>
          <p className="mt-1 text-xs">
            {runnerOffline ? (
              <Trans>
                Playwright and Lighthouse runs are executed by apps/testing-workers — a local-only
                Docker container that is not deployed anywhere. Nothing is draining the queues, so
                runs are refused immediately instead of streaming nothing. Start it on the machine
                that has the browsers, then re-check.
              </Trans>
            ) : (
              <Trans>
                The API could not check whether a runner is listening, so runs are still allowed. If
                the console stays silent, the container is most likely not running.
              </Trans>
            )}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="min-h-[2.75rem]"
              onClick={() => void refetchRunner()}
              disabled={runnerQuery.isFetching}
            >
              {runnerQuery.isFetching ? <Trans>Checking…</Trans> : <Trans>Re-check</Trans>}
            </Button>
            <code className="font-mono text-[0.625rem] break-all">
              docker compose --profile testing up testing-workers
            </code>
          </div>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={(v) => switchTab(v as TestingTab)}>
        <TabsList>
          <TabsTrigger value="run">
            <Trans>Run</Trans>
          </TabsTrigger>
          <TabsTrigger value="history">
            <Trans>History</Trans>
            {history.length > 0 && (
              <span className="bg-muted-foreground/20 ml-1.5 rounded-full px-1.5 text-[0.625rem]">
                {history.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="lighthouse">
            <Trans>Lighthouse</Trans>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="run" className="space-y-6 pt-4">
          <div className="bg-card rounded-xl border p-4">
            <h2 className="mb-3 text-sm font-semibold">
              <Trans>Playwright suite</Trans>
            </h2>
            <div className="mb-3 flex flex-wrap gap-2">
              {(["public", "account", "admin", "all"] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setSuite(s)}
                  disabled={isRunning}
                  className={`min-h-[2.75rem] rounded-full px-4 py-1 text-xs font-medium transition-colors ${
                    suite === s
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  } ${isRunning ? "cursor-not-allowed opacity-50" : ""}`}
                >
                  {s}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={() => void runPlaywright()} disabled={isRunning}>
                {isRunning && !activeRerun ? (
                  <Trans>Running...</Trans>
                ) : (
                  <Trans>Run Playwright</Trans>
                )}
              </Button>
              {isRunning && (
                <Button size="sm" variant="destructive" onClick={cancel}>
                  <Trans>Cancel</Trans>
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={clearResults} disabled={isRunning}>
                <Trans>Clear</Trans>
              </Button>
            </div>
            {playwrightStats && (
              <div className="mt-3 flex flex-wrap gap-2">
                <Badge variant="success">
                  <Trans>{playwrightStats.passed} passed</Trans>
                </Badge>
                {playwrightStats.failed > 0 && (
                  <Badge variant="destructive">
                    <Trans>{playwrightStats.failed} failed</Trans>
                  </Badge>
                )}
                <Badge variant="outline">
                  <Trans>{playwrightStats.total} total</Trans>
                </Badge>
              </div>
            )}
          </div>

          <div className="bg-card rounded-xl border">
            <div className="flex items-center justify-between border-b px-4 py-2">
              <h2 className="text-sm font-semibold">
                <Trans>Live log</Trans>
              </h2>
              <Button size="sm" variant="ghost" onClick={copyLogs} disabled={logs.length === 0}>
                <Trans>Copy</Trans>
              </Button>
            </div>
            <div ref={logRef} className="h-72 overflow-y-auto p-3 font-mono text-[0.6875rem]">
              {logs.length === 0 ? (
                <div className="text-muted-foreground py-8 text-center">
                  <Trans>No output yet. Run a suite to start streaming.</Trans>
                </div>
              ) : (
                logs.map((l, i) => (
                  <div
                    key={i}
                    className={
                      l.type === "error"
                        ? "text-destructive-strong"
                        : l.type === "success"
                          ? "text-success-strong"
                          : l.type === "progress"
                            ? "text-warning-strong"
                            : "text-muted-foreground"
                    }
                  >
                    <span className="text-muted-foreground/60">[{l.time}]</span> {l.message}
                  </div>
                ))
              )}
            </div>
          </div>

          {playwrightResults.length > 0 && (
            <div className="bg-card rounded-xl border">
              <div className="border-b px-4 py-2">
                <h2 className="text-sm font-semibold">
                  <Trans>Playwright results</Trans>
                </h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="px-3 py-2 font-medium">
                        <Trans>Test</Trans>
                      </th>
                      <th className="px-3 py-2 text-center font-medium">
                        <Trans>Status</Trans>
                      </th>
                      <th className="hidden px-3 py-2 text-right font-medium sm:table-cell">
                        <Trans>Duration</Trans>
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        <span className="sr-only">
                          <Trans>Actions</Trans>
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {playwrightResults.map((r, i) => (
                      <tr key={i} className="hover:bg-muted/30 border-b last:border-0">
                        <td className="px-3 py-1.5 font-mono text-[0.625rem]">{r.test}</td>
                        <td className="px-3 py-1.5 text-center">
                          {r.status === "passed" ? (
                            <Badge variant="success">✓</Badge>
                          ) : r.status === "failed" ? (
                            <Badge variant="destructive">✗</Badge>
                          ) : (
                            <Badge variant="outline">…</Badge>
                          )}
                        </td>
                        <td className="text-muted-foreground hidden px-3 py-1.5 text-right sm:table-cell">
                          {r.duration ? `${r.duration}ms` : "—"}
                        </td>
                        <td className="px-3 py-1.5 text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => void runPlaywright(r.test)}
                            disabled={isRunning}
                            aria-label={t`Re-run ${r.test}`}
                            className="min-h-0 px-2 py-1 text-[0.625rem]"
                          >
                            {activeRerun === r.test ? (
                              <Trans>Running…</Trans>
                            ) : (
                              <Trans>Re-run</Trans>
                            )}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="history" className="pt-4">
          <HistoryPanel history={history} onChange={setHistory} />
        </TabsContent>

        <TabsContent value="lighthouse" className="pt-4">
          <LighthousePanel env={dbTarget} onComplete={onLighthouseComplete} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
